import { useEffect, useRef, useState } from 'react';

// Full-cover loading overlay shown on mount and hidden the first time a
// hosted game reports it's finished loading. Games signal completion via
// either a `document` event (same-origin) or `window.postMessage` (cross-
// origin iframes on a different port). We accept both. The matching logic
// mirrors ReelsStack so callers don't have to reconfigure the event name
// in two places — just pass the same `loadedEventName`.
//
// Only the FIRST matching signal hides the preloader; subsequent
// game.loaded events from later reels in the TikTok stack are ignored
// here (they're still handled by ReelsStack for appending next frames).

export interface ApplicationLoadedDetail {
  title?: string;
  id?: string;
  gameCode?: string;
  [key: string]: unknown;
}

export interface PreloaderProps {
  /**
   * Event name the hosted game dispatches when it finishes loading.
   * Default: `'game.loaded'`. Variants (`GAME_LOADED`, `gameLoaded`) are
   * also accepted, matching the Application.ts contract.
   */
  loadedEventName?: string;
  /** Text shown under the spinner. Default: `'Loading…'`. */
  label?: string;
  /**
   * Called when `application.loaded` is received from the preloader script,
   * with the parsed `application.json` payload. Use this to read the game
   * title, id, etc. before the game itself finishes loading.
   */
  onApplicationLoaded?: (detail: ApplicationLoadedDetail) => void;
  /**
   * URL of an avatar image shown above the spinner. Omit to hide.
   * Consumers typically import a bundled asset and pass its URL here
   * (e.g. `import avatar from '.../image.png'`).
   */
  avatar?: string;
  /** Accessible alt text for the avatar image. Default: `''` (decorative). */
  avatarAlt?: string;
  /**
   * Optional custom content rendered in place of the default spinner +
   * label. Receives no props — use a static node.
   */
  children?: React.ReactNode;
  /**
   * Fade-out duration in ms. Default: `400`. The overlay is unmounted
   * after this timeout so it no longer blocks pointer events.
   */
  fadeMs?: number;
  /**
   * Minimum time the preloader stays visible before it's allowed to
   * hide, in ms. Prevents a jarring flash when the game loads
   * instantly (e.g. from cache). Default: `0` (no minimum).
   */
  minVisibleMs?: number;
  /**
   * Extra delay in ms added after `game.loaded` fires, before the slide
   * animation starts. Gives the game time to finish GPU-heavy work
   * (texture uploads, shader compilation, first frame render) so the
   * content is visually ready when the preloader moves away.
   * Default: `500`.
   */
  postLoadDelayMs?: number;
  /**
   * Force the preloader visible regardless of loaded events. Useful for
   * Storybook / manual QA. Default: `false`.
   */
  forceVisible?: boolean;
  /** Called once the fade-out finishes and the overlay is unmounted. */
  onHidden?: () => void;
  /** Called the first time a `game.loaded` signal is heard. */
  onGameLoaded?: () => void;
}

// Same variants ReelsStack accepts — keep the two in sync.
const DEFAULT_VARIANTS = ['game.loaded', 'GAME_LOADED', 'gameLoaded'];

export function Preloader({
  loadedEventName = 'game.loaded',
  avatar,
  avatarAlt = '',
  children,
  fadeMs = 400,
  minVisibleMs = 0,
  postLoadDelayMs = 500,
  forceVisible = false,
  onHidden,
  onGameLoaded,
  onApplicationLoaded,
}: PreloaderProps) {
  // Three visual states:
  //   'visible'  — covering the UI, opaque
  //   'fading'   — game.loaded received, opacity animating to 0
  //   'hidden'   — fade complete, component returns null
  const [phase, setPhase] = useState<'visible' | 'fading' | 'hidden'>('visible');
  // True as soon as game.loaded fires — content fades out during postLoadDelayMs.
  const [contentDismissed, setContentDismissed] = useState(false);
  // `targetProgressRef` is the last *real* percent reported via
  // 'loading.progress' messages — tracked as a ref (not state) since we
  // don't need a render on every raw update, only when the smoothed/
  // trickled `displayProgress` below actually moves.
  const targetProgressRef = useRef(0);
  const [displayProgress, setDisplayProgress] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const mountedAt = useRef<number>(Date.now());
  // Guard so only the first matching event triggers the hide.
  const triggered = useRef(false);
  // If the game loaded before minVisibleMs elapsed, we stash the pending
  // hide in a timer and let it fire when the minimum is up.
  const pendingHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable refs for callbacks — keeps them out of effect deps so a new
  // function identity on the parent re-render never cancels a pending timer.
  const onGameLoadedRef = useRef(onGameLoaded);
  const onHiddenRef = useRef(onHidden);
  const onApplicationLoadedRef = useRef(onApplicationLoaded);
  useEffect(() => { onGameLoadedRef.current = onGameLoaded; });
  useEffect(() => { onHiddenRef.current = onHidden; });
  useEffect(() => { onApplicationLoadedRef.current = onApplicationLoaded; });

  // Listen for the preloader script's `application.loaded` event, which
  // carries the parsed application.json payload. Use it to show the game
  // title while the rest of the assets are still downloading.
  //
  // The event fires during window.onload — before the React app mounts —
  // so we also check window.application on mount as a fallback for the
  // case where the event has already been dispatched by the time this
  // effect runs.
  useEffect(() => {
    const applyDetail = (detail: ApplicationLoadedDetail) => {
      onApplicationLoadedRef.current?.(detail);
    };

    // postMessage from the game iframe (cross-origin, different document)
    const onMessage = (e: MessageEvent) => {
      const d = e.data;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'application.loaded') {
        applyDetail((d.detail as ApplicationLoadedDetail) ?? {});
      }
      if (d.type === 'loading.progress' && typeof d.percent === 'number') {
        targetProgressRef.current = Math.max(targetProgressRef.current, d.percent);
      }
      if (d.type === 'loading.error') {
        setLoadError(typeof d.message === 'string' && d.message ? d.message : 'Failed to load game');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Smoothly eases `displayProgress` toward the last real target instead of
  // snapping to it, and — since the real signal has a long dead zone
  // between the last resource-loaded event (in practice landing around
  // 85%, see sources/preloader/playmer.js: 5% initial + N script loads +
  // 5 resource-type events with nothing reported during the CPU-heavy
  // component-creation phase that follows) and the eventual game.loaded —
  // trickles forward on its own once it catches up to the target, so the
  // bar never visibly freezes. The trickle ceiling stays a bounded amount
  // ahead of the last real target (and short of 100%) so it can't overtake
  // genuine progress or falsely claim completion before game.loaded fires.
  useEffect(() => {
    if (phase === 'hidden') return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(now - last, 100); // clamp so a tab-switch stall doesn't cause one huge jump
      last = now;
      setDisplayProgress((current) => {
        const target = targetProgressRef.current;
        if (current < target) {
          // Ease toward a real, higher target — the step scales with how
          // far behind we are, so a big jump (e.g. 25% -> 85%) catches up
          // in a fraction of a second instead of crawling at a fixed rate.
          const step = Math.max((target - current) * 0.1, 0.08) * (dt / 16.7);
          return Math.min(current + step, target);
        }
        // Caught up to the last real target — the "stuck" case. Trickle
        // forward slowly (~0.3%/100ms) capped at target+12 (and never
        // above 98%) purely so it reads as still-alive during the dead zone.
        const ceiling = Math.min(target + 12, 98);
        return Math.min(current + 0.003 * dt, ceiling);
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  useEffect(() => {
    if (forceVisible) return;

    const variants = new Set(
      [loadedEventName, ...DEFAULT_VARIANTS].map((s) => String(s).toLowerCase()),
    );
    const matches = (raw: unknown): boolean => {
      if (typeof raw === 'string') return variants.has(raw.toLowerCase());
      if (raw && typeof raw === 'object') {
        const t = (raw as { type?: unknown }).type;
        if (typeof t === 'string') return variants.has(t.toLowerCase());
      }
      return false;
    };

    const beginHide = () => {
      if (triggered.current) return;
      triggered.current = true;
      setContentDismissed(true);
      // Game genuinely finished — let the smoothing effect sweep the
      // display value up to 100 rather than snapping, but stop trickling
      // it anywhere else once we know the real answer.
      targetProgressRef.current = 100;

      const elapsed = Date.now() - mountedAt.current;
      const wait = Math.max(0, minVisibleMs - elapsed) + postLoadDelayMs;

      const startFade = () => {
        // Fire onGameLoaded here — not before the minVisibleMs wait — so
        // consumers that animate on this callback (e.g. the SlotUI game
        // slide-in) start in the same frame as the preloader slide-out.
        onGameLoadedRef.current?.();
        setPhase('fading');
        fadeTimer.current = setTimeout(() => {
          setPhase('hidden');
          onHiddenRef.current?.();
        }, fadeMs);
      };

      if (wait > 0) {
        pendingHideTimer.current = setTimeout(startFade, wait);
      } else {
        startFade();
      }
    };

    const onDocEvent = () => beginHide();
    // Attach a listener for every accepted casing so the preloader hides
    // regardless of which variant the game dispatches.
    variants.forEach((v) => document.addEventListener(v, onDocEvent));

    // Cross-origin iframes (different port = different origin) can't touch
    // the parent document directly, so we also accept postMessage with
    // either the raw string or `{ type: <name> }`.
    const onMessage = (e: MessageEvent) => {
      if (matches(e.data)) beginHide();
    };
    window.addEventListener('message', onMessage);

    return () => {
      variants.forEach((v) => document.removeEventListener(v, onDocEvent));
      window.removeEventListener('message', onMessage);
      if (pendingHideTimer.current) clearTimeout(pendingHideTimer.current);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    };
  }, [loadedEventName, fadeMs, minVisibleMs, postLoadDelayMs, forceVisible]);

  if (phase === 'hidden') return null;

  return (
    <div
      aria-hidden={phase === 'fading'}
      role="status"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1000,
        overflow: 'hidden',
        transform: phase === 'fading' ? 'translateY(-100%)' : 'translateY(0)',
        transition: `transform ${fadeMs}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        pointerEvents: phase === 'fading' ? 'none' : 'auto',
        userSelect: 'none',
        // Base gradient
        background: 'radial-gradient(224.88% 76.67% at -44.53% 100%, #FE47BA 35.58%, #FE37B4 44.23%, #630426 94.23%)',
        color: '#fff',
      }}
    >
      {/* Dark overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(977.1% 186.8% at 31.09% 0%, rgba(9,4,24,0) 0%, #090418 100%)',
        pointerEvents: 'none',
      }} />

      {/*
        Status-bar-height strip, matching the one in index.html's static
        pre-React preloader (see the comments there for the full story,
        including the theme-color meta tag): the base gradient above
        originates bottom-left, so by design its color at the very top
        edge resolves to solid maroon (#630426) — which is what Safari 26
        actually reads for its status-bar tint (it no longer honors the
        `theme-color` meta tag at all; it reads background-color off
        fixed/sticky elements near each viewport edge instead). The dark
        overlay above is centered near the top too, so it barely darkens
        that same strip. `backgroundColor` is set explicitly alongside the
        gradient `backgroundImage` since that's specifically the property
        Safari's algorithm reads; the gradient is purely so this fades into
        the artwork instead of a hard-edged band. Height is a small flat
        value rather than tied to `env(safe-area-inset-top)` — the sample
        only needs y=0 to be dark, and sizing this to the full safe-area
        height (~60px+ on Dynamic Island phones) made the visible fade band
        unnecessarily tall.
      */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 2,
        backgroundColor: '#0F1619',
        backgroundImage: 'linear-gradient(#0F1619, transparent)',
        pointerEvents: 'none',
      }} />

      {/*
        Same idea, bottom edge — controls Safari 26's bottom *toolbar*
        tint. Requires `viewport-fit=cover` on the page's viewport meta tag
        (set in index.html) for `env(safe-area-inset-bottom)` to resolve to
        a real value instead of 0. Also a small flat height, same reasoning
        as the top strip.
      */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: 2,
        backgroundColor: '#0F1619',
        backgroundImage: 'linear-gradient(to top, #0F1619, transparent)',
        pointerEvents: 'none',
      }} />

      {/* Pink glow blob — upper-left */}
      <div style={{
        position: 'absolute',
        width: '100%', height: '46%',
        top: '6%', left: '-32%',
        background: 'linear-gradient(159.24deg, #FE47BA 34.39%, #FE37B4 71.74%, #B70156 109.81%)',
        borderRadius: '50%',
        opacity: 0.7,
        filter: 'blur(100px)',
        transform: 'rotate(-108deg)',
        pointerEvents: 'none',
      }} />

      {/* Blue/dark glow blob — lower */}
      <div style={{
        position: 'absolute',
        width: '75%', height: '34%',
        bottom: '10%', left: '-10%',
        background: 'linear-gradient(180deg, #090418 0%, #1419B9 100%)',
        borderRadius: '50%',
        filter: 'blur(150px)',
        pointerEvents: 'none',
      }} />

      {children ?? (
        <>
          {/* Logo + wordmark block — vertically centered, biased slightly above middle */}
          <div style={{
            position: 'absolute',
            top: '28%',
            left: 0, right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 20,
          }}>
            {avatar
              ? (
                <img
                  src={avatar} alt={avatarAlt} draggable={false}
                  style={{ width: 110, height: 110, objectFit: 'contain' }}
                />
              )
              : <ReelayMiddleLogo />
            }
          </div>

          {/* Bottom logo — fixed to bottom */}
          <div style={{ position: 'absolute', bottom: 31, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
            <ReelayBottomLogo />
          </div>

          {/* Progress bar + game name — anchored to lower third */}
          <div style={{
            position: 'absolute',
            bottom: '14%',
            left: 0, right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            {loadError
              ? (
                <div style={{
                  width: '60%', maxWidth: 260,
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 10, color: '#FF6B6B', textAlign: 'center',
                }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path d="M12 2L2 20h20L12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                    <path d="M12 9v5M12 16.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <div style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.9 }}>
                    Failed to load
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.55, wordBreak: 'break-word' }}>{loadError}</div>
                </div>
              )
              : (
                <div style={{
                  width: '100%', display: 'flex', justifyContent: 'center',
                  opacity: contentDismissed ? 0 : 1,
                  transition: `opacity ${postLoadDelayMs * 0.6}ms ease`,
                }}>
                  <ProgressBar percent={displayProgress} />
                </div>
              )
            }
          </div>
        </>
      )}
    </div>
  );
}

function ProgressBar({ percent }: { percent: number }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: '60%' }}>
      {/* track — knob overlaps top/bottom, so the wrapper needs room to breathe */}
      <div style={{ position: 'relative', width: '100%', padding: '10px 0' }}>
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: 10,
            borderRadius: 20,
            background: '#271951',
            overflow: 'visible',
          }}
        >
          {/* fill */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${clamped}%`,
              borderRadius: 20,
              background: '#FE37B4',
              // No CSS transition here — displayProgress is already eased
              // frame-by-frame in JS (see the smoothing effect above), so a
              // second CSS-level transition would just add lag on top.
            }}
          />
          {/* knob showing the live percentage */}
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: `${clamped}%`,
              transform: 'translate(-50%, -50%)',
              minWidth: 48,
              height: 29,
              boxSizing: 'border-box',
              padding: '6px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#FE37B4',
              borderRadius: 50,
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: 14,
              lineHeight: '17px',
              color: '#FFFFFF',
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {Math.round(clamped)}%
          </div>
        </div>
      </div>
    </div>
  );
}

// Middle logo — combined icon + wordmark shown above the loading bar.
function ReelayMiddleLogo() {
  return (
    <svg width="248" height="48" viewBox="0 0 712 138" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Reelay">
      <path d="M132.888 61.7891C134.689 61.7891 136.204 63.1605 136.288 64.9599C136.338 66.0275 136.363 67.1016 136.363 68.1816L136.357 69.0625C135.886 106.312 105.543 136.363 68.1816 136.363C67.812 136.361 67.5813 135.962 67.7634 135.64L76.9366 119.431C77.4414 118.539 78.316 117.92 79.3161 117.696C101.983 112.621 118.923 92.3799 118.923 68.1816C118.923 67.3589 118.903 66.5406 118.865 65.7274C118.765 63.6307 120.379 61.7891 122.478 61.7891H132.888ZM20.2903 45.5063C21.2979 46.5355 21.5584 48.0739 21.0253 49.4119C18.7123 55.2177 17.4404 61.5511 17.4404 68.1816C17.4405 91.6282 33.3438 111.359 54.9518 117.179C56.5453 117.608 57.707 119.021 57.707 120.671V131.494C57.707 133.633 55.7959 135.273 53.7059 134.821C23.2801 128.243 0.414831 101.36 0.00585938 69.0625L0 68.1816C0 57.4021 2.50147 47.2069 6.95568 38.1447C7.97369 36.0736 10.7174 35.7274 12.3318 37.3765L20.2903 45.5063ZM69.0625 0.00585938C99.7057 0.393887 125.476 20.9972 133.653 49.0931C134.481 51.9372 131.437 54.1126 128.873 52.6285L113.055 43.4704C112.522 43.1623 112.083 42.7172 111.768 42.1894C102.909 27.3666 86.7049 17.4405 68.1816 17.4404C55.2867 17.4404 43.5156 22.2512 34.564 30.1752C33.6414 30.992 32.3738 31.3181 31.187 30.9865L19.3948 27.6915C17.0048 27.0237 16.0657 24.148 17.7357 22.3124C30.2071 8.60462 48.1895 0 68.1816 0L69.0625 0.00585938Z" fill="white"/>
      <path d="M41.4872 64.6947L13.0039 36.699C10.8545 34.5863 7.21819 36.0101 7.07465 39.0205L6.09112 59.6476C6.01193 61.3084 7.11603 62.7942 8.72908 63.1976L38.1959 70.5663C41.6435 71.4284 44.0217 67.1858 41.4872 64.6947Z" fill="white"/>
      <path d="M90.7915 42.0067L129.208 52.9257C132.107 53.7497 134.616 50.7572 133.299 48.0464L124.276 29.4715C123.55 27.9759 121.869 27.201 120.26 27.6201L90.866 35.276C87.427 36.1717 87.3732 41.0352 90.7915 42.0067Z" fill="white"/>
      <path d="M84.284 97.7688L64.9349 132.707C63.4748 135.343 65.82 138.466 68.759 137.798L88.8965 133.224C90.5179 132.856 91.654 131.394 91.6108 129.732L90.8223 99.3681C90.7301 95.8156 86.0057 94.66 84.284 97.7688Z" fill="white"/>
      <path d="M195.749 31.2H176V104.284H195.749V65.338C195.749 49.9519 217.907 49.9519 246.326 49.9519V32.1616C207.31 29.7576 203.456 35.5274 195.749 43.2204V31.2Z" fill="white"/>
      <path d="M317.601 28.6185C337.352 28.6185 353.363 44.63 353.363 64.3812V70.181C353.363 71.1571 353.325 72.1241 353.248 73.0804H275.555C275.555 74.3692 277.488 80.3304 277.488 80.3304C279.035 86.1295 288.765 88.8677 293.437 89.5121H315.667C324.173 89.5121 329.844 83.3913 331.616 80.3304H351.903C347.529 95.1383 333.826 105.944 317.601 105.944H291.503C271.752 105.944 255.74 89.9323 255.74 70.181V64.3812C255.74 44.6301 271.752 28.6187 291.503 28.6185H317.601ZM293.921 46.5004C280.389 46.5004 276.039 54.2331 276.039 59.5492C294.081 59.3881 330.549 59.1627 332.1 59.5492C332.1 53.2665 325.334 46.5004 317.118 46.5004H293.921Z" fill="white"/>
      <path d="M426.825 28.6185C446.576 28.6187 462.588 44.6301 462.588 64.3812V70.181C462.588 71.1571 462.548 72.1241 462.472 73.0804H384.779C384.779 74.3692 386.712 80.3304 386.712 80.3304C388.259 86.1296 397.989 88.8677 402.661 89.5121H424.892C433.397 89.5121 439.067 83.3914 440.84 80.3304H461.127C456.752 95.1382 443.051 105.944 426.825 105.944H400.728C380.976 105.944 364.965 89.9324 364.965 70.181V64.3812C364.965 44.6301 380.976 28.6186 400.728 28.6185H426.825ZM403.142 46.5004C389.61 46.5004 385.26 54.2331 385.26 59.5492C403.302 59.3881 439.775 59.1626 441.321 59.5492C441.321 53.2665 434.555 46.5004 426.339 46.5004H403.142Z" fill="white"/>
      <rect x="476.609" y="5" width="19.3053" height="99.2842" fill="white"/>
      <path d="M557.311 29.5854C602.739 31.0352 603.706 49.8826 603.706 60.0317V104.977H584.375L583.892 95.7953C580.348 99.0172 568.523 105.557 549.578 105.944C525.897 106.427 508.016 104.977 508.016 83.7133C508.016 62.4488 523.964 57.1323 549.578 57.1323C570.069 57.1323 581.314 62.6095 584.375 65.3481L584.377 65.3315C585.023 60.1663 586.573 47.7562 564.56 46.9838C542.522 46.2106 521.225 50.5273 516.231 52.7827V36.351C523.577 31.7115 546.678 29.9076 557.311 29.5854ZM552.495 70.6479C541.63 70.2599 535.352 71.8306 531.713 74.0561C528.037 76.3045 527.105 79.1897 526.85 81.2348C526.694 82.4824 526.707 83.7609 527.21 84.9633C527.718 86.1772 528.699 87.2536 530.363 88.142C533.652 89.8974 539.716 90.9789 550.544 90.9789C572.252 90.9789 581.844 85.8141 584.717 83.1108L584.854 82.9809L584.872 82.7924C584.953 81.8989 585.051 80.5094 585.079 79.2426C585.093 78.6102 585.091 77.9986 585.059 77.4916C585.043 77.2385 585.019 77.0023 584.984 76.7993C584.951 76.6055 584.903 76.4041 584.821 76.2407L584.778 76.1557L584.708 76.0922C584.231 75.6633 583.314 75.172 581.928 74.6616C580.524 74.1449 578.592 73.592 576.042 73.0629C570.941 72.0046 563.34 71.0352 552.495 70.6479Z" fill="white"/>
      <path d="M636.139 31.2H617.304C617.304 39.0667 617.304 58.942 617.303 75.5101C616.337 95.7386 632.275 105.371 659.32 105.371C680.956 105.371 690.55 98.3073 692.643 94.7754C693.766 120.302 681.052 119.242 660.769 119.82C647.246 120.205 630.343 116.449 623.582 114.522V130.898C648.695 136.196 660.286 137.159 686.365 134.751C707.228 132.824 711.478 114.362 710.995 105.371V31.2H692.16V63.9509C692.16 76.4734 692.546 88.0325 667.047 88.0325C641.548 88.0325 636.139 84.1795 636.139 66.8407V31.2Z" fill="white"/>
    </svg>
  );
}

function ReelayBottomLogo() {
  return (
    <svg width="123" height="43" viewBox="0 0 614 213" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M19.8058 21.706V66.9133C19.8058 68.7635 20.9959 70.4929 22.8641 70.659C35.1814 71.7543 46.4483 54.5157 59.2393 43.2496C60.7504 41.9186 62.7222 41.2436 64.7481 41.2436H83.6247C89.7865 41.2436 86.5589 44.8614 84.1748 46.6704L49.5146 79.2311C67.67 89.9288 65.4693 97.6822 88.026 119.932C92.4273 124.273 89.8598 126.444 88.026 126.987H68.6266C66.9765 126.987 65.4137 126.257 64.4057 124.968C61.9079 121.775 57.3381 115.703 51.1651 106.908C41.2622 92.7981 34.6602 92.4864 28.6084 89.9288C25.1035 88.4476 22.7057 89.4625 21.3084 90.5791C20.146 91.5079 19.8058 93.0297 19.8058 94.5053V121.56C19.8058 124.557 17.3427 126.987 14.3042 126.987H5.50162C2.46316 126.987 0 124.557 0 121.56V22.2498C0 9.96159 10.099 0 22.5567 0H30.2589C33.2974 0 35.7606 2.42966 35.7606 5.42679V13.4594C35.7606 16.4565 33.2974 18.8862 30.2589 18.8862H22.6646C21.0857 18.8862 19.8058 20.1487 19.8058 21.706Z" fill="white"/>
      <rect x="261.05" y="41.1852" width="19.4653" height="85.8019" rx="1.11889" fill="white"/>
      <path d="M359.577 40.3193C364.874 40.3194 368.244 44.3638 368.686 49.5673C369.724 52.9465 369.036 56.7062 366.543 59.3867L319.61 109.841H363.001C366.163 109.841 368.726 112.435 368.726 115.635V124.904C368.726 128.104 366.163 130.697 363.001 130.697H297.734C292.043 130.697 288.574 126.029 288.574 120.269C288.574 119.287 288.678 118.337 288.873 117.436C288.605 114.665 289.447 111.839 291.437 109.7L336.574 61.1757H294.312C291.15 61.1756 288.587 58.5821 288.587 55.3828V46.1132C288.587 42.9137 291.15 40.3194 294.312 40.3193H359.577Z" fill="white"/>
      <path d="M475.02 40.3193C496.343 40.3193 513.629 57.5905 513.629 78.8954V129.299C513.629 130.071 513.003 130.697 512.23 130.697H494.068C493.296 130.697 492.67 130.071 492.67 129.299V78.3437C492.67 68.9089 485.014 61.2607 475.571 61.2607C466.128 61.2607 458.473 68.9089 458.473 78.3437V129.299C458.473 130.071 457.847 130.697 457.074 130.697H437.8C437.028 130.697 436.401 130.071 436.401 129.299V78.3437C436.401 68.9089 428.746 61.2607 419.303 61.2607C409.86 61.2609 402.205 68.909 402.205 78.3437V129.299C402.205 130.071 401.578 130.697 400.806 130.697H381.541C380.769 130.697 380.143 130.071 380.143 129.299V78.8954C380.143 57.5905 397.429 40.3193 418.752 40.3193C429.845 40.3194 439.842 44.9952 446.885 52.4794C453.927 44.9946 463.926 40.3193 475.02 40.3193Z" fill="white"/>
      <path d="M568.478 40.3193C593.456 40.3193 613.706 60.5515 613.706 85.5087C613.706 86.7922 613.649 88.0629 613.544 89.3193H613.706V129.299C613.706 130.071 613.08 130.697 612.308 130.697H594.397C593.625 130.697 592.999 130.071 592.999 129.299V123.997C592.999 123.775 592.754 123.642 592.566 123.76C585.592 128.154 577.332 130.697 568.478 130.697C543.499 130.697 523.25 110.466 523.25 85.5087C523.25 60.5517 543.499 40.3195 568.478 40.3193ZM569.069 58.5771C553.567 58.5771 541 71.1332 541 86.622C541 102.111 553.567 114.667 569.069 114.667C584.572 114.667 597.139 102.111 597.139 86.622C597.139 71.1332 584.571 58.5771 569.069 58.5771Z" fill="white"/>
      <path d="M244.558 41.0488C249.818 41.0488 254.083 45.313 254.083 50.5732C254.083 55.8336 249.818 60.0986 244.558 60.0986H235.789C224.984 60.0987 216.225 68.8573 216.225 79.6621V96.1377C216.225 113.483 202.152 127.544 184.792 127.544C173.292 127.544 163.235 121.372 157.756 112.163C156.893 110.89 156.177 109.401 155.589 107.775C154.212 104.328 153.429 100.58 153.365 96.6572C152.954 91.9158 153.074 87.3522 153.355 84.2969C153.355 71.2171 141.82 60.6133 127.591 60.6133C113.361 60.6134 101.826 71.2172 101.826 84.2969C101.826 97.3765 113.362 107.979 127.591 107.979H141.137C141.909 107.979 142.534 108.606 142.534 109.378V124.03C142.534 124.625 142.159 125.157 141.594 125.341C137.192 126.77 132.484 127.544 127.591 127.544C103.116 127.544 83.2755 108.182 83.2754 84.2969C83.2754 60.4121 103.116 41.0499 127.591 41.0498C152.065 41.0498 171.906 60.4114 171.906 84.2959H171.909V95.623C171.909 102.447 177.446 107.979 184.276 107.979C191.106 107.979 196.643 102.447 196.644 95.623V66.2236C196.644 52.3201 207.915 41.0491 221.818 41.0488H244.558Z" fill="white"/>
      <path d="M583.359 151C600.281 151 614 164.719 614 181.641C614 198.563 600.281 212.28 583.359 212.28H442.641C425.719 212.28 412 198.563 412 181.641C412 164.719 425.719 151 442.641 151H583.359ZM443.384 167.523C440.35 167.523 437.589 168.152 435.119 169.422C432.675 170.667 430.746 172.435 429.348 174.716C427.946 176.977 427.25 179.535 427.25 182.367C427.25 185.345 427.929 187.947 429.311 190.141L429.312 190.146C430.71 192.318 432.57 193.979 434.881 195.121C437.183 196.258 439.709 196.823 442.443 196.823C445.148 196.823 447.503 196.324 449.485 195.306V196.434H455.144V181.225H448.831V189.411C448.08 189.97 447.223 190.406 446.253 190.714L446.249 190.715C445.199 191.056 444.094 191.229 442.934 191.229C441.285 191.228 439.777 190.886 438.401 190.207C437.038 189.508 435.938 188.499 435.1 187.168C434.302 185.833 433.889 184.227 433.889 182.328C433.889 180.535 434.301 178.961 435.109 177.59L435.108 177.589C435.946 176.215 437.084 175.157 438.53 174.407L438.534 174.406C440.01 173.631 441.704 173.235 443.629 173.234C446.545 173.234 449.015 174.211 451.072 176.17L451.512 176.59L455.771 172.632L455.343 172.168C453.937 170.638 452.203 169.48 450.154 168.694C448.109 167.91 445.849 167.523 443.384 167.523ZM559.038 168.784C556.364 168.784 553.959 169.372 551.826 170.548C549.727 171.724 548.067 173.371 546.849 175.488C545.664 177.572 545.07 179.926 545.07 182.547C545.07 185.202 545.681 187.588 546.899 189.705C548.152 191.789 549.913 193.419 552.182 194.595C554.45 195.771 557.074 196.359 560.054 196.359C562.39 196.359 564.489 195.989 566.352 195.25C568.214 194.511 569.738 193.419 570.923 191.974L567.57 188.092C565.607 190.041 563.152 191.016 560.206 191.016C558.208 191.016 556.498 190.612 555.076 189.806C553.654 188.999 552.622 187.823 551.979 186.277L572.751 182.244C572.751 179.522 572.142 177.152 570.923 175.136C569.704 173.086 568.044 171.523 565.945 170.447C563.88 169.338 561.577 168.784 559.038 168.784ZM588.497 168.784C584.841 168.784 581.929 169.557 579.762 171.103C577.629 172.615 576.562 174.649 576.562 177.203C576.562 179.152 577.07 180.681 578.086 181.79C579.135 182.865 580.354 183.622 581.742 184.059C583.164 184.496 584.993 184.883 587.228 185.219C589.36 185.555 590.902 185.908 591.85 186.277C592.831 186.647 593.322 187.302 593.322 188.243C593.322 190.226 591.426 191.218 587.634 191.218C585.975 191.218 584.281 190.982 582.555 190.512C580.862 190.008 579.406 189.352 578.188 188.546L575.749 193.335C577.002 194.209 578.678 194.932 580.777 195.503C582.876 196.074 585.027 196.359 587.228 196.359C591.019 196.359 594.016 195.603 596.217 194.091C598.418 192.579 599.519 190.562 599.519 188.041C599.518 186.126 599.011 184.647 597.995 183.606C597.013 182.564 595.828 181.824 594.439 181.387C593.051 180.95 591.273 180.564 589.106 180.228C586.94 179.858 585.332 179.472 584.282 179.068C583.267 178.632 582.758 177.908 582.758 176.9C582.758 175.993 583.233 175.27 584.181 174.732C585.162 174.161 586.585 173.875 588.446 173.875C591.324 173.875 593.915 174.548 596.217 175.892L598.655 171.103C597.47 170.397 595.946 169.843 594.084 169.44C592.222 169.003 590.359 168.784 588.497 168.784ZM473.607 169.657C471.084 169.657 468.819 170.203 466.812 171.294C464.839 172.385 463.285 173.926 462.152 175.916C461.052 177.874 460.502 180.168 460.502 182.8C460.502 185.431 461.052 187.742 462.152 189.732C463.285 191.721 464.839 193.262 466.812 194.354C468.819 195.444 471.084 195.99 473.607 195.99C475.387 195.99 477.006 195.685 478.462 195.075C479.95 194.466 481.196 193.551 482.199 192.331V195.653H487.976V169.946H481.908V173.075C480.873 171.952 479.643 171.101 478.219 170.523C476.827 169.946 475.29 169.657 473.607 169.657ZM528.652 169.657C526.614 169.657 524.753 170.042 523.07 170.813C521.388 171.551 520.012 172.594 518.944 173.941C518.071 172.529 516.872 171.471 515.352 170.765C513.863 170.027 512.148 169.657 510.206 169.657C508.427 169.657 506.809 169.963 505.353 170.572C503.896 171.182 502.683 172.065 501.712 173.22V169.946H495.936V195.653H502.003V182.607C502.003 180.104 502.602 178.194 503.799 176.878C505.028 175.562 506.679 174.904 508.75 174.904C510.627 174.904 512.067 175.483 513.07 176.638C514.074 177.761 514.575 179.446 514.575 181.692V195.653H520.643V182.607C520.643 180.137 521.258 178.243 522.487 176.927C523.717 175.579 525.351 174.905 527.39 174.904C529.266 174.904 530.707 175.483 531.71 176.638C532.713 177.761 533.215 179.446 533.215 181.692V195.653H539.282V180.922C539.282 177.167 538.328 174.359 536.419 172.498C534.51 170.605 531.921 169.657 528.652 169.657ZM474.287 174.76C476.52 174.76 478.365 175.498 479.821 176.975C481.277 178.451 482.006 180.393 482.006 182.8C482.006 185.207 481.277 187.149 479.821 188.625C478.365 190.101 476.52 190.839 474.287 190.839C472.055 190.839 470.21 190.101 468.754 188.625C467.33 187.149 466.618 185.207 466.618 182.8C466.618 180.393 467.33 178.451 468.754 176.975C470.21 175.499 472.055 174.76 474.287 174.76ZM559.038 173.825C560.934 173.825 562.542 174.33 563.862 175.338C565.217 176.346 566.115 177.674 566.555 179.32L551.216 182.244V182.193C551.216 179.639 551.928 177.606 553.35 176.094C554.805 174.582 556.702 173.825 559.038 173.825Z" fill="white"/>
    </svg>
  );
}
